// Zeiterfassung — alle Rechenregeln an EINER Stelle, ohne DB-Zugriff (vitest).
//
// Grundbegriffe:
//  - Stempel  = ein Arbeitsabschnitt (Kommen bis Gehen), optional mit Pausen daran.
//  - Brutto   = Gehen minus Kommen.
//  - Pause    = die gestempelte Pause, mindestens aber die gesetzliche (§4 ArbZG).
//  - Netto    = Brutto minus Pause. Das ist die Ist-Arbeitszeit.
//  - Soll     = Vorgabe des Arbeitszeitmodells für den Wochentag.
//  - Saldo    = Ist minus Soll (positiv = Überstunden, negativ = Minusstunden).

import { istFeiertag, feiertagName } from './feiertage';
import { abwesenheitAmTag, TYP_INFO, type Abwesenheit } from './abwesenheit';

export interface Pause {
  beginn: string;
  ende: string | null;
}

export interface Stempel {
  id: string;
  mitarbeiter_id: string;
  beginn: string;
  ende: string | null;
  quelle?: string | null;
  notiz?: string | null;
  pausen?: Pause[];
}

/** Soll-Minuten je Wochentag, 1 = Montag … 7 = Sonntag. */
export type SollProTag = Record<string, number>;

export interface Arbeitszeitmodell {
  id: string;
  name: string;
  soll_minuten: SollProTag;
  urlaubstage_jahr?: number;
  /** Vorlage für Abrechnung nach Stunden (#536) — belegt den Schalter im Dialog vor. */
  nach_stunden?: boolean;
}

/** Ausgezahlte Überstunden — sie verlassen das Stundenkonto zum Auszahlungsdatum. */
export interface Auszahlung {
  datum: string; // 'YYYY-MM-DD'
  minuten: number;
}

export interface Zuordnung {
  gueltig_ab: string; // 'YYYY-MM-DD'
  modell_id: string;
  start_saldo_minuten: number;
  /** Abweichender Urlaubsanspruch; ohne Angabe gilt der des Modells. */
  urlaubstage_jahr?: number | null;
  /**
   * Persönliche Arbeitszeit je Wochentag. Hat Vorrang vor dem Modell — krumme
   * Zeiten wie 22 Wochenstunden gehören an die Person, nicht in ein eigenes Modell
   * (Entscheidung Björn 2026-08-13). Ohne Angabe gilt weiterhin das Modell.
   */
  soll_minuten?: SollProTag | null;
  /**
   * Abrechnung nach Stunden (#536, z. B. Minijob ohne feste Zeiten): kein festes Soll,
   * jede gearbeitete Minute gilt als erfüllt. Stundenkonto bleibt unberührt, keine
   * Mehrarbeit, im Lohnexport alles Grundstunden.
   */
  nach_stunden?: boolean | null;
}

/** Gilt am Tag die Abrechnung nach Stunden? */
export function nachStundenAm(zuordnungen: Zuordnung[], tag: string): boolean {
  return !!zuordnungAm(zuordnungen, tag)?.nach_stunden;
}

// ── Grundrechnungen ──────────────────────────────────────────────────────────

/** Minuten zwischen zwei Zeitpunkten; ohne Ende zählt `jetzt` (laufender Stempel). */
export function dauerMinuten(beginn: string, ende: string | null, jetzt: Date = new Date()): number {
  const a = new Date(beginn).getTime();
  const b = ende ? new Date(ende).getTime() : jetzt.getTime();
  if (Number.isNaN(a) || Number.isNaN(b)) return 0;
  return Math.max(0, Math.round((b - a) / 60000));
}

/**
 * Gesetzliche Mindestpause nach §4 ArbZG: ab mehr als 6 Stunden 30 Minuten,
 * ab mehr als 9 Stunden 45 Minuten. Bezugsgröße ist die ARBEITSZEIT ohne Pause,
 * deshalb wird sie unten iterativ auf die Bruttozeit angewandt.
 */
export function gesetzlichePauseMinuten(arbeitsminuten: number): number {
  if (arbeitsminuten > 9 * 60) return 45;
  if (arbeitsminuten > 6 * 60) return 30;
  return 0;
}

/** Summe der gestempelten Pausen eines Abschnitts (laufende Pause zählt mit). */
export function gestempeltePause(stempel: Stempel, jetzt: Date = new Date()): number {
  return (stempel.pausen || []).reduce((s, p) => s + dauerMinuten(p.beginn, p.ende, jetzt), 0);
}

/**
 * Anzurechnende Pause: die gestempelte, mindestens aber die gesetzliche.
 * Die gesetzliche richtet sich nach der Arbeitszeit NACH Abzug — deshalb wird
 * zuerst mit Brutto gerechnet und das Ergebnis gegengeprüft (zwei Schritte reichen,
 * weil die Stufen 6 h und 9 h weit auseinanderliegen).
 */
export function anzurechnendePause(bruttoMinuten: number, gestempelt: number): number {
  let pause = Math.max(gestempelt, gesetzlichePauseMinuten(bruttoMinuten));
  const nachAbzug = bruttoMinuten - pause;
  const zweiterVersuch = Math.max(gestempelt, gesetzlichePauseMinuten(nachAbzug));
  if (zweiterVersuch < pause) pause = zweiterVersuch;
  return Math.min(pause, bruttoMinuten);
}

/** Netto-Arbeitszeit eines Abschnitts in Minuten. */
export function nettoMinuten(stempel: Stempel, jetzt: Date = new Date()): number {
  const brutto = dauerMinuten(stempel.beginn, stempel.ende, jetzt);
  return Math.max(0, brutto - anzurechnendePause(brutto, gestempeltePause(stempel, jetzt)));
}

// ── Tage und Modelle ─────────────────────────────────────────────────────────

/** Kalendertag eines Zeitpunkts als 'YYYY-MM-DD' (lokale Zeit — der Arbeitstag ist lokal). */
export function tagVon(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Wochentag eines Kalendertags: 1 = Montag … 7 = Sonntag. */
export function wochentag(tag: string): number {
  const d = new Date(`${tag}T12:00:00`);
  const w = d.getDay();
  return w === 0 ? 7 : w;
}

/** Das am Stichtag gültige Modell (jüngste Zuordnung mit gueltig_ab <= Tag). */
export function zuordnungAm(zuordnungen: Zuordnung[], tag: string): Zuordnung | null {
  const passende = zuordnungen
    .filter((z) => z.gueltig_ab <= tag)
    .sort((a, b) => (a.gueltig_ab < b.gueltig_ab ? 1 : -1));
  return passende[0] || null;
}

/**
 * Soll-Minuten für einen Kalendertag; ohne Zeiten 0 (kein Soll = kein Minus).
 * An gesetzlichen Feiertagen ist das Soll null — sonst erzeugte jeder Feiertag
 * automatisch Minusstunden.
 *
 * Reihenfolge: persönliche Zeiten der Zuordnung vor denen des Modells. Das Modell
 * ist damit nur noch Vorlage; krumme Wochenstunden brauchen kein eigenes Modell.
 */
export function sollMinutenAmTag(
  tag: string,
  zuordnungen: Zuordnung[],
  modelle: Arbeitszeitmodell[],
): number {
  if (istFeiertag(tag)) return 0;
  const z = zuordnungAm(zuordnungen, tag);
  if (!z || z.nach_stunden) return 0;
  const quelle = z.soll_minuten ?? modelle.find((m) => m.id === z.modell_id)?.soll_minuten;
  const wert = quelle?.[String(wochentag(tag))];
  return typeof wert === 'number' && Number.isFinite(wert) ? Math.max(0, wert) : 0;
}

// ── Tages- und Monatsauswertung ──────────────────────────────────────────────

export interface Tageszeile {
  tag: string;
  istMinuten: number;
  pauseMinuten: number;
  sollMinuten: number;
  saldoMinuten: number;
  stempel: Stempel[];
  nachgetragen: boolean;
  laufend: boolean;
  /** Gesetzlicher Feiertag (Hessen) — Name oder null */
  feiertag: string | null;
  /** Am Tag wirksame Abwesenheit (genehmigt oder beantragt) */
  abwesenheit: Abwesenheit | null;
  /** Gutgeschriebene Minuten aus Urlaub/Krank (zählen im Ist mit) */
  gutschriftMinuten: number;
}

/** Alle Abschnitte eines Tages zusammenfassen (mehrere Kommen/Gehen sind normal). */
export function tageszeile(
  tag: string,
  stempel: Stempel[],
  zuordnungen: Zuordnung[],
  modelle: Arbeitszeitmodell[],
  jetzt: Date = new Date(),
  abwesenheiten: Abwesenheit[] = [],
): Tageszeile {
  const meine = stempel.filter((s) => tagVon(s.beginn) === tag)
    .sort((a, b) => (a.beginn < b.beginn ? -1 : 1));
  const istMinuten = meine.reduce((s, x) => s + nettoMinuten(x, jetzt), 0);
  const pauseMinuten = meine.reduce((s, x) => {
    const brutto = dauerMinuten(x.beginn, x.ende, jetzt);
    return s + anzurechnendePause(brutto, gestempeltePause(x, jetzt));
  }, 0);
  const sollRoh = sollMinutenAmTag(tag, zuordnungen, modelle);

  // Abwesenheiten wirken NUR, wenn sie genehmigt sind — ein offener Urlaubsantrag
  // soll das Stundenkonto noch nicht schönrechnen.
  const abwesenheit = abwesenheitAmTag(abwesenheiten, tag);
  const wirksam = abwesenheit?.status === 'genehmigt' ? abwesenheit : null;
  const wirkung = wirksam ? TYP_INFO[wirksam.typ]?.wirkung : 'keine';
  const anteil = wirksam?.halber_tag ? 0.5 : 1;

  // Nach Stunden: Soll = was gearbeitet wurde → Tagessaldo 0, keine Mehrarbeit.
  let sollMinuten = nachStundenAm(zuordnungen, tag) ? istMinuten : sollRoh;
  let gutschriftMinuten = 0;
  if (wirkung === 'soll_erfuellt') gutschriftMinuten = Math.round(sollRoh * anteil);
  else if (wirkung === 'soll_null') sollMinuten = Math.round(sollRoh * (1 - anteil));
  // 'abbau' (Freizeitausgleich): Soll bleibt, keine Gutschrift → der Tag zieht vom
  // Stundenkonto ab, genau das ist der Zweck.

  const istGesamt = istMinuten + gutschriftMinuten;
  return {
    tag,
    istMinuten: istGesamt,
    pauseMinuten,
    sollMinuten,
    saldoMinuten: istGesamt - sollMinuten,
    stempel: meine,
    nachgetragen: meine.some((s) => s.quelle === 'manuell'),
    laufend: meine.some((s) => !s.ende),
    feiertag: feiertagName(tag),
    abwesenheit,
    gutschriftMinuten,
  };
}

/** Alle Kalendertage eines Monats ('YYYY-MM') als 'YYYY-MM-DD'. */
export function tageImMonat(monat: string): string[] {
  const [j, m] = monat.split('-').map(Number);
  if (!j || !m) return [];
  const anzahl = new Date(j, m, 0).getDate();
  return Array.from({ length: anzahl }, (_, i) => `${monat}-${String(i + 1).padStart(2, '0')}`);
}

export interface Monatsbilanz {
  monat: string;
  zeilen: Tageszeile[];
  istMinuten: number;
  sollMinuten: number;
  saldoMinuten: number;
}

/** Monatsübersicht — nur Tage mit Buchungen ODER Soll erscheinen in den Zeilen. */
export function monatsbilanz(
  monat: string,
  stempel: Stempel[],
  zuordnungen: Zuordnung[],
  modelle: Arbeitszeitmodell[],
  jetzt: Date = new Date(),
  abwesenheiten: Abwesenheit[] = [],
): Monatsbilanz {
  const zeilen = tageImMonat(monat)
    .map((t) => tageszeile(t, stempel, zuordnungen, modelle, jetzt, abwesenheiten))
    .filter((z) => z.stempel.length > 0 || z.sollMinuten > 0 || z.abwesenheit || z.feiertag);
  const istMinuten = zeilen.reduce((s, z) => s + z.istMinuten, 0);
  const sollMinuten = zeilen.reduce((s, z) => s + z.sollMinuten, 0);
  return { monat, zeilen, istMinuten, sollMinuten, saldoMinuten: istMinuten - sollMinuten };
}

/**
 * Gesamtsaldo bis einschließlich `bis`: Startübertrag plus alle Tagessalden.
 * Zukünftige Tage zählen NICHT mit — sonst stünde man am Monatsersten mit dem
 * Soll des ganzen Monats im Minus.
 */
export function gesamtsaldo(
  stempel: Stempel[],
  zuordnungen: Zuordnung[],
  modelle: Arbeitszeitmodell[],
  bis: string,
  jetzt: Date = new Date(),
  abwesenheiten: Abwesenheit[] = [],
  auszahlungen: Auszahlung[] = [],
): number {
  const start = zuordnungen.length
    ? [...zuordnungen].sort((a, b) => (a.gueltig_ab < b.gueltig_ab ? -1 : 1))[0].start_saldo_minuten
    : 0;
  const tage = new Set<string>();
  for (const s of stempel) tage.add(tagVon(s.beginn));
  const fruehesteZuordnung = zuordnungen.length
    ? [...zuordnungen].sort((a, b) => (a.gueltig_ab < b.gueltig_ab ? -1 : 1))[0].gueltig_ab
    : null;
  if (fruehesteZuordnung) {
    // Sollstunden auch an Tagen OHNE Buchung (sonst fehlt das Minus für vergessene Tage).
    for (let d = new Date(`${fruehesteZuordnung}T12:00:00`); tagVon(d.toISOString()) <= bis; d.setDate(d.getDate() + 1)) {
      tage.add(tagVon(d.toISOString()));
    }
  }
  let saldo = start;
  for (const t of [...tage].sort()) {
    if (t > bis) continue;
    saldo += tageszeile(t, stempel, zuordnungen, modelle, jetzt, abwesenheiten).saldoMinuten;
  }
  // Ausgezahlte Stunden sind abgegolten und gehören nicht mehr zum Guthaben.
  for (const a of auszahlungen) {
    if (a.datum <= bis) saldo -= Math.max(0, a.minuten);
  }
  return saldo;
}

/**
 * Mehrarbeit eines Tages = alles über dem Soll. Grundlage für die Frage, ob für
 * einen Tag eine Genehmigung vorliegt.
 */
export function mehrarbeitMinuten(zeile: Tageszeile): number {
  return Math.max(0, zeile.istMinuten - zeile.sollMinuten);
}

// ── Anzeige ──────────────────────────────────────────────────────────────────

/** 495 → „8:15 h"; negative Werte mit Vorzeichen („−1:30 h"). */
export function stundenText(minuten: number, mitVorzeichen = false): string {
  const negativ = minuten < 0;
  const abs = Math.abs(Math.round(minuten));
  const text = `${Math.floor(abs / 60)}:${String(abs % 60).padStart(2, '0')} h`;
  if (negativ) return `−${text}`;
  return mitVorzeichen && abs > 0 ? `+${text}` : text;
}

/** „08:15" aus einem Zeitpunkt. */
export function uhrzeit(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
}

/** „Mo, 12.08." für die Tageszeile. */
export function tagText(tag: string): string {
  const d = new Date(`${tag}T12:00:00`);
  return Number.isNaN(d.getTime()) ? tag : d.toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit' });
}
